import { __rest, __assign } from '../../../node_modules/tslib/tslib.es6.js';
import React__default__default, { useContext, useState, useRef, useEffect } from 'react';
import PropTypes__default from 'prop-types';
import styles from '../autocomplete.scss.js';
import HoneyBem from '../../../packages/honey-bem/honey-bem.js';
import { AutocompleteOption } from '../quarks/autocomplete-option.js';
import { AutocompleteOptionGroup, AutocompleteGroupName } from '../quarks/autocomplete-option-group.js';
import { AutocompleteContext, AutocompleteOptionsContext } from '../autocomplete-context.js';
import { Injecss } from '../../../packages/injecss/injecss.js';

var bem = HoneyBem.init('autocomplete', styles);
var AutocompleteOptions = function (_a) {
  var label = _a.label,
    _b = _a.appearance,
    appearance = _b === void 0 ? 'boxed' : _b,
    _c = _a.extraClasses,
    extraClasses = _c === void 0 ? '' : _c,
    _d = _a.onBlur,
    onBlur = _d === void 0 ? function () {
      return null;
    } : _d,
    _e = _a.optionHasSubtitle,
    optionHasSubtitle = _e === void 0 ? true : _e,
    _f = _a.optionProps,
    optionProps = _f === void 0 ? {} : _f,
    _g = _a.optionsToDisplay,
    optionsToDisplay = _g === void 0 ? 3 : _g,
    renderOption = _a.renderOption,
    rest = __rest(_a, ["label", "appearance", "extraClasses", "onBlur", "optionHasSubtitle", "optionProps", "optionsToDisplay", "renderOption"]);
  // autocomplete contexts
  var _h = useContext(AutocompleteContext),
    containerId = _h.containerId,
    focusedOption = _h.focusedOption;
  var _j = useContext(AutocompleteOptionsContext),
    options = _j.options,
    listBoxRef = _j.listBoxRef;
  // dropdown height
  var _k = useState(0),
    dropHeight = _k[0],
    setDropHeight = _k[1];
  // height of the option item (button)
  var _l = useState(0),
    optionHeight = _l[0],
    setOptionHeight = _l[1];
  // storing refs for option items in order to gain control on focus if needed
  var optionRefs = useRef([]);
  // resets option refs for next render
  optionRefs.current = [];
  // set dropdown height depending on option height
  useEffect(function () {
    setDropHeight(optionHeight * optionsToDisplay);
  }, [optionsToDisplay, optionHeight]);
  // scroll options container if visually focused option is hidden
  useEffect(function () {
    var _a, _b, _c;
    if (focusedOption !== undefined && focusedOption >= 0 && optionRefs.current[focusedOption]) {
      var listRect = (_a = listBoxRef === null || listBoxRef === void 0 ? void 0 : listBoxRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
      var itemRect = (_b = optionRefs.current[focusedOption]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
      if (listRect && itemRect && (itemRect.y + itemRect.height > listRect.y + listRect.height || itemRect.y < listRect.y)) {
        (_c = listBoxRef === null || listBoxRef === void 0 ? void 0 : listBoxRef.current) === null || _c === void 0 ? void 0 : _c.scroll(listRect.x, listBoxRef.current.scrollTop + (itemRect.bottom - listRect.height - listRect.top));
      }
    }
  }, [focusedOption]);
  // grouped options
  var optionGroups = options && options.map(function (option, optionIndex) {
    if ('options' in option && option.options.length > 0) {
      return /*#__PURE__*/React__default__default.createElement(AutocompleteOptionGroup, {
        bem: bem,
        appearance: appearance,
        key: "".concat(option.key, "-list"),
        groupName: /*#__PURE__*/React__default__default.createElement(AutocompleteGroupName, {
          bem: bem,
          id: "".concat(containerId, "-groupName-").concat(optionIndex),
          key: "".concat(option.key, "-title")
        }, option.group)
      }, option.options.map(function (entry) {
        return /*#__PURE__*/React__default__default.createElement(AutocompleteOption, __assign({
          bem: bem,
          key: entry.key || entry.title,
          option: entry,
          optionHasSubtitle: optionHasSubtitle,
          renderOption: renderOption,
          ref: function ($el) {
            // populating an array of option item refs
            if ($el) {
              optionRefs.current.push($el);
            }
            return $el && setOptionHeight($el.getBoundingClientRect().height + 1);
          }
        }, optionProps));
      }));
    }
    return null;
  }).filter(function (item) {
    return item !== null;
  });
  var ungroupedOptions = options && options.map(function (option) {
    if (!('options' in option)) {
      return /*#__PURE__*/React__default__default.createElement(AutocompleteOption, __assign({
        bem: bem,
        key: option.key || option.title,
        option: option,
        optionHasSubtitle: optionHasSubtitle,
        renderOption: renderOption,
        ref: function ($el) {
          // populating an array of option item refs
          if ($el) {
            optionRefs.current.push($el);
          }
          return $el && setOptionHeight($el.getBoundingClientRect().height + 1);
        }
      }, optionProps));
    }
    return null;
  }).filter(function (item) {
    return item !== null;
  });
  return /*#__PURE__*/React__default__default.createElement("div", __assign({
    id: "".concat(containerId, "-listbox"),
    "aria-label": label,
    style: {
      maxHeight: "".concat(dropHeight, "px")
    },
    className: bem.e('dropdown', {
      boxed: appearance === 'boxed',
      flat: appearance === 'flat'
    }, extraClasses),
    onBlur: onBlur,
    ref: listBoxRef,
    role: "listbox"
  }, rest), optionGroups, (ungroupedOptions === null || ungroupedOptions === void 0 ? void 0 : ungroupedOptions.length) ? /*#__PURE__*/React__default__default.createElement(AutocompleteOptionGroup, {
    bem: bem,
    appearance: appearance
  }, ungroupedOptions) : null);
};
AutocompleteOptions.displayName = 'AutocompleteOptions';
AutocompleteOptions.propTypes = {
  appearance: PropTypes__default.oneOf(['boxed', 'flat']),
  extraClasses: PropTypes__default.string,
  label: PropTypes__default.string.isRequired,
  onBlur: PropTypes__default.func,
  optionHasSubtitle: PropTypes__default.bool,
  optionProps: PropTypes__default.object,
  optionsToDisplay: PropTypes__default.number,
  renderOption: PropTypes__default.func
};
var autocompleteOptions = Injecss(AutocompleteOptions, styles);

export { AutocompleteOptions, autocompleteOptions as default };
